/**
 * StageIndicator — what the assistant is doing RIGHT NOW (SCRUM-281).
 *
 * Replaces the bare loading dot with the pipeline step the server says is
 * running: a small glyph whose motion says what kind of work it is, and a
 * label that says it in words. The server emits a stage immediately BEFORE the
 * step it names and the store clears it the moment content arrives, so what
 * this renders is, by construction, the step running at that moment — no
 * timers, no scripted sequence, nothing invented on the client.
 *
 * Two placements, one component:
 *   - `lead`     — before any text, where the dots were.
 *   - `trailing` — under text that has finished streaming, while the server
 *                  does post-answer work ("Checking sellers…", "Preparing next
 *                  steps…"). The caller hides the caret then: writing is done.
 *
 * Motion follows `@/lib/motion`: transform + opacity only, enters ease out,
 * exits run faster. On a stage change the old label rolls up and out while
 * the new one rises in — progress you can see without a fake step counter.
 * Under reduced motion the glyph is static and the label swaps instantly; the
 * `.g-shimmer` sweep pins to readable text (styles/tailwind.css).
 *
 * Accessibility: ONE visually-hidden polite status node carries the label.
 * The visible layers are aria-hidden, because during the roll two labels are
 * briefly in the DOM and a live region over both would read both.
 *
 * DESIGN-SYSTEM CALLOUT (GUARDRAILS rule 10): neither the prototype
 * (docs/GardEn/src/screens/Chat.jsx — three `TypingDots`) nor the 2026-08-16
 * assistant design draws a stage state. This is drafted inside the system —
 * `.g-shimmer` (whose own comment anticipates "named work"), the avatar's
 * primary tile, lucide icons, the house motion vocabulary — and flagged for
 * design review rather than presented as canon.
 */
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type TargetAndTransition,
  type Transition,
} from "framer-motion";
import {
  BookOpen,
  FileText,
  Lightbulb,
  PenLine,
  ReceiptText,
  Search,
  Sparkles,
  Store,
  type LucideIcon,
} from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { MOTION } from "@/lib/motion";
import {
  isKnownStage,
  stageLabelKey,
  type KnownStage,
} from "@/features/chat/chat-stage";
import type { StageEvent } from "@/features/chat/useChatStream";

interface GlyphSpec {
  Icon: LucideIcon;
  /** A looping keyframe set, or `"scan"` — a line sweeping the page. */
  loop: { animate: TargetAndTransition; transition: Transition } | "scan";
}

const EASE_LOOP = { repeat: Infinity, ease: "easeInOut" } as const;

const BREATHE: GlyphSpec["loop"] = {
  animate: { scale: [1, 1.12, 1], opacity: [0.75, 1, 0.75] },
  transition: { duration: 1.6, ...EASE_LOOP },
};

/** Each stage's icon and the motion that says what kind of work it is. */
const GLYPHS: Record<KnownStage | "working", GlyphSpec> = {
  understanding: { Icon: Sparkles, loop: BREATHE },
  // A small diamond orbit — the shyrdak unit, traced while it looks.
  searching_products: {
    Icon: Search,
    loop: {
      animate: { x: [0, 1.8, 0, -1.8, 0], y: [-1.8, 0, 1.8, 0, -1.8] },
      transition: { duration: 1.4, repeat: Infinity, ease: "linear" },
    },
  },
  checking_sellers: {
    Icon: Store,
    loop: { animate: { y: [0, -1.5, 0] }, transition: { duration: 1.2, ...EASE_LOOP } },
  },
  reading_document: { Icon: FileText, loop: "scan" },
  reading_quote: { Icon: ReceiptText, loop: "scan" },
  searching_knowledge: {
    Icon: BookOpen,
    loop: {
      animate: { y: [0, -1, 0], opacity: [0.8, 1, 0.8] },
      transition: { duration: 1.6, ...EASE_LOOP },
    },
  },
  composing: {
    Icon: PenLine,
    loop: {
      animate: { rotate: [0, -8, 0, 6, 0], x: [0, 1, 0, -1, 0] },
      transition: { duration: 0.9, ...EASE_LOOP },
    },
  },
  suggesting_next: {
    Icon: Lightbulb,
    loop: {
      animate: { opacity: [0.6, 1, 0.6], scale: [1, 1.08, 1] },
      transition: { duration: 1.4, ...EASE_LOOP },
    },
  },
  working: { Icon: Sparkles, loop: BREATHE },
};

const SIZES = {
  lead: {
    root: "inline-flex gap-2.5",
    tile: "size-6 rounded-[8px]",
    icon: 14,
    scanTravel: 10,
    label: "text-[15px] font-medium",
  },
  trailing: {
    root: "mt-2.5 flex gap-2",
    tile: "size-5 rounded-[7px]",
    icon: 12,
    scanTravel: 8,
    label: "text-[14px]",
  },
} as const;

const EXIT = { duration: 0.15, ease: MOTION.ease.out };
const ENTER: Transition = { duration: MOTION.dur.bubble, ease: MOTION.ease.out };

export interface StageIndicatorProps {
  stage: StageEvent;
  variant: "lead" | "trailing";
  /** Inside the ≥1200px design frame — paint the tile from `--gd-*`. */
  design?: boolean;
  /** The classifier's intent for this turn, exposed as `data-intent`
   * (STORY-002-009 AC#3's captured payload now that the label names a stage). */
  intent?: string | null;
}

export function StageIndicator({
  stage,
  variant,
  design = false,
  intent = null,
}: StageIndicatorProps): ReactElement {
  const { t } = useTranslation("chat");
  const reduced = useReducedMotion() ?? false;
  const { key, values } = stageLabelKey(stage);
  const label = values !== undefined ? t(key, values) : t(key);
  const glyphKey: KnownStage | "working" = isKnownStage(stage.key)
    ? stage.key
    : "working";
  const { Icon, loop } = GLYPHS[glyphKey];
  const size = SIZES[variant];

  return (
    <span
      className={size.root + " items-center"}
      data-testid="chat-stage"
      data-stage={stage.key}
      data-variant={variant}
      data-motion={reduced ? "static" : "animated"}
      {...(intent !== null ? { "data-intent": intent } : {})}
    >
      <span className="sr-only" role="status" aria-live="polite">
        {label}
      </span>
      <span
        aria-hidden="true"
        className={
          "relative grid shrink-0 place-items-center overflow-hidden " +
          size.tile +
          (design
            ? " bg-[var(--gd-wash-2)] text-[var(--gd-primary)]"
            : " bg-primary/10 text-primary")
        }
      >
        <AnimatePresence>
          <motion.span
            key={glyphKey}
            className="absolute inset-0 grid place-items-center"
            initial={reduced ? false : { opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={
              reduced
                ? { opacity: 0, transition: { duration: 0 } }
                : { opacity: 0, scale: 0.6, transition: EXIT }
            }
            transition={ENTER}
          >
            {reduced || loop === "scan" ? (
              <Icon size={size.icon} strokeWidth={2} />
            ) : (
              <motion.span
                className="grid place-items-center"
                animate={loop.animate}
                transition={loop.transition}
              >
                <Icon size={size.icon} strokeWidth={2} />
              </motion.span>
            )}
            {!reduced && loop === "scan" ? (
              <motion.span
                className="pointer-events-none absolute inset-x-[5px] top-[22%] h-px rounded-full bg-current"
                animate={{ y: [0, size.scanTravel, 0], opacity: [0, 1, 0] }}
                transition={{ duration: 1.4, ...EASE_LOOP }}
              />
            ) : null}
          </motion.span>
        </AnimatePresence>
      </span>
      <span
        aria-hidden="true"
        className={"relative grid overflow-hidden " + size.label}
      >
        <AnimatePresence>
          <motion.span
            key={label}
            className="g-shimmer col-start-1 row-start-1"
            initial={reduced ? false : { y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={
              reduced
                ? { opacity: 0, transition: { duration: 0 } }
                : { y: -10, opacity: 0, transition: EXIT }
            }
            transition={ENTER}
          >
            {label}
          </motion.span>
        </AnimatePresence>
      </span>
    </span>
  );
}

export default StageIndicator;
